import i18n from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import en from "@/locales/en.json";
import tr from "@/locales/tr.json";

/**
 * Flatten a nested translation object into dot-keyed entries.
 * Example: { admin: { save: "Kaydet" } } → [["admin.save", "Kaydet"]]
 */
export function flattenTranslations(
  obj: Record<string, unknown>,
  prefix = ""
): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out.push(...flattenTranslations(v as Record<string, unknown>, key));
    } else if (typeof v === "string") {
      out.push([key, v]);
    }
  }
  return out;
}

/** Set a dot-keyed value into a nested object (mutates target). */
export function setDeep(target: Record<string, any>, dottedKey: string, value: string) {
  const parts = dottedKey.split(".");
  let cur = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (typeof cur[p] !== "object" || cur[p] === null || Array.isArray(cur[p])) {
      cur[p] = {};
    }
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
}

// ---------------- Cache ----------------
// We cache the merged bundles in localStorage keyed by a "version" derived from
// MAX(updated_at) + row count of ui_translations. On boot we issue ONE tiny
// query for the version; if it matches the cached version we skip the heavy
// fetch+merge entirely. The cache is also invalidated automatically whenever
// the static JSON files change (via STATIC_VERSION).
const CACHE_KEY = "i18n_db_overrides_cache_v1";
// Bump this when en.json / tr.json change shape so old caches are invalidated.
// (Cheap approximation — full hashing isn't worth the bundle cost.)
const STATIC_VERSION = `${Object.keys(en).length}.${Object.keys(tr).length}`;

interface CachedBundle {
  version: string;          // `${maxUpdatedAt}|${rowCount}|${STATIC_VERSION}`
  cachedAt: number;
  mergedTr: Record<string, any>;
  mergedEn: Record<string, any>;
}

function readCache(): CachedBundle | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBundle;
    if (!parsed?.version || !parsed.mergedTr || !parsed.mergedEn) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(bundle: CachedBundle) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(bundle));
  } catch (e) {
    // Quota exceeded or storage disabled — silently degrade.
    console.warn("[i18n] cache write failed:", e);
  }
}

export function clearTranslationsCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

function applyBundles(mergedTr: Record<string, any>, mergedEn: Record<string, any>) {
  i18n.addResourceBundle("tr", "translation", mergedTr, true, true);
  i18n.addResourceBundle("en", "translation", mergedEn, true, true);
  i18n.emit("languageChanged", i18n.language);
}

/**
 * Lightweight version probe: returns `${maxUpdatedAt}|${count}|${STATIC_VERSION}`.
 * Uses HEAD count + a single ORDER BY updated_at DESC LIMIT 1 row.
 * Returns null if the probe failed (caller should fall back to full fetch).
 */
async function fetchVersion(): Promise<string | null> {
  try {
    const [latest, countRes] = await Promise.all([
      supabase
        .from("ui_translations")
        .select("updated_at")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("ui_translations")
        .select("*", { count: "exact", head: true }),
    ]);
    if (latest.error) {
      console.warn("[i18n] version probe (latest) failed:", latest.error.message);
      return null;
    }
    if (countRes.error) {
      console.warn("[i18n] version probe (count) failed:", countRes.error.message);
      return null;
    }
    const maxUpdated = latest.data?.updated_at ?? "0";
    const count = countRes.count ?? 0;
    return `${maxUpdated}|${count}|${STATIC_VERSION}`;
  } catch (e) {
    console.warn("[i18n] version probe error:", e);
    return null;
  }
}

async function fetchAndMerge(): Promise<{ mergedTr: Record<string, any>; mergedEn: Record<string, any> } | null> {
  const { data, error } = await supabase
    .from("ui_translations")
    .select("key, value_tr, value_en");
  if (error) {
    console.warn("[i18n] Failed to load DB overrides:", error.message);
    return null;
  }
  const mergedTr: Record<string, any> = JSON.parse(JSON.stringify(tr));
  const mergedEn: Record<string, any> = JSON.parse(JSON.stringify(en));
  if (data && data.length > 0) {
    for (const row of data as any[]) {
      if (!row.key) continue;
      if (row.value_tr && row.value_tr.length > 0) setDeep(mergedTr, row.key, row.value_tr);
      if (row.value_en && row.value_en.length > 0) setDeep(mergedEn, row.key, row.value_en);
    }
  }
  return { mergedTr, mergedEn };
}

/**
 * Apply DB overrides into i18next bundles.
 *
 * Strategy:
 *  1. If a localStorage cache exists, apply it IMMEDIATELY (zero-flash boot).
 *  2. Probe the DB version with a tiny query.
 *  3. If version matches cache → done. No full fetch, no merge.
 *  4. If version differs (or no cache) → fetch all rows, merge, apply, cache.
 *
 * Pass `{ force: true }` after admin edits to bypass the cache and re-fetch.
 */
export async function applyDbTranslationOverrides(opts: { force?: boolean } = {}) {
  const { force = false } = opts;
  const cached = readCache();

  // 1) Hydrate UI from cache instantly (only if we trust the static fingerprint).
  if (cached && cached.version.endsWith(`|${STATIC_VERSION}`)) {
    applyBundles(cached.mergedTr, cached.mergedEn);
  }

  try {
    // 2) Cheap version probe.
    const remoteVersion = force ? null : await fetchVersion();

    // 3) Cache hit → nothing else to do.
    if (!force && remoteVersion && cached && remoteVersion === cached.version) {
      return;
    }

    // 4) Cache miss → full fetch + merge.
    const merged = await fetchAndMerge();
    if (!merged) return;

    applyBundles(merged.mergedTr, merged.mergedEn);

    // Re-probe after the fetch so the cached version reflects the data we just
    // wrote (avoids racing with a concurrent admin edit).
    const finalVersion = remoteVersion ?? (await fetchVersion()) ?? `unknown-${Date.now()}|${STATIC_VERSION}`;
    writeCache({
      version: finalVersion,
      cachedAt: Date.now(),
      mergedTr: merged.mergedTr,
      mergedEn: merged.mergedEn,
    });
  } catch (e) {
    console.warn("[i18n] applyDbTranslationOverrides error:", e);
  }
}
